/** @jsxImportSource @emotion/react */
import React, { useState, useReducer, useEffect } from "react";
import {
  FormGroup,
  FormControl,
  FormLabel,
  FormText,
  Alert,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { Modal as ModalJS } from "components/molecules/Modal.molecule";
import { FormButton as FormButtonJS } from "components-old/modal-elems";
import { ReportDTO } from "../../models/ReportDTO";
import { useSendEmailReport } from "pages/reports/bi-dashboard-next/hooks/useSendEmailReport";

interface ModalProps {
  show: boolean;
  onHide: () => void;
  backdrop?: string;
  "data-qa"?: string;
  children?: React.ReactNode;
}

interface ModalHeaderProps {
  title: string;
  isLoading?: boolean;
}

interface ModalBodyProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

interface ModalFooterProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const Modal: React.FC<ModalProps> & {
  Header: React.FC<ModalHeaderProps>;
  Body: React.FC<ModalBodyProps>;
  Footer: React.FC<ModalFooterProps>;
} = ModalJS as any;

interface FormButtonProps {
  actionType?: string;
  clickHandler: () => void;
  disabled?: boolean;
  label: string;
  style?: React.CSSProperties;
  "data-qa"?: string;
}

const FormButton: React.FC<FormButtonProps> = FormButtonJS as any;

interface EmailReportModalProps {
  show: boolean;
  hide: () => void;
  report: ReportDTO;
}

interface EmailFormState {
  email: string;
  body: string;
}

type EmailFormAction =
  | { type: "EMAIL"; payload: string }
  | { type: "BODY"; payload: string }
  | { type: "RESET"; payload: EmailFormState };

const initialEmailFormState: EmailFormState = {
  email: "",
  body: "",
};

const formDataReducer = (
  state: EmailFormState,
  action: EmailFormAction,
): EmailFormState => {
  switch (action.type) {
    case "EMAIL":
      return { ...state, email: action.payload };
    case "BODY":
      return { ...state, body: action.payload };
    case "RESET":
      return action.payload;
    default:
      return state;
  }
};

const formatEmail = (emails: string): string[] => {
  return emails.split(";").map((singleEmail) => singleEmail.trim());
};

const validateEmailForm = (
  data: EmailFormState,
  t: (key: string) => string,
) => {
  if (!data.email) {
    return { error: true, errorText: t('reports:"Send to" is required') };
  } else if (!data.body) {
    return { error: true, errorText: t('reports:"Body" is required') };
  }

  return { error: false, errorText: "" };
};

const EmailForm: React.FC<{
  email: string;
  body: string;
  dispatch: React.Dispatch<EmailFormAction>;
}> = ({ email, body, dispatch }) => {
  const { t } = useTranslation("reports");

  return (
    <>
      <FormGroup css={{ marginBottom: "1rem" }}>
        <FormLabel>
          {t("reports:Send to")}
          <sup>*</sup>
        </FormLabel>
        <FormControl
          data-qa="email-input"
          value={email}
          onChange={(e) => dispatch({ type: "EMAIL", payload: e.target.value })}
        />
        <FormText muted style={{ fontSize: "80%" }}>
          {t(
            "reports:Multiple emails can be entered by separating each with a semicolon",
          )}
        </FormText>
      </FormGroup>
      <FormGroup css={{ marginBottom: "1rem" }}>
        <FormLabel>
          {t("reports:Body")}
          <sup>*</sup>
        </FormLabel>
        <FormControl
          data-qa="email-body-input"
          as="textarea"
          value={body}
          onChange={(e) => dispatch({ type: "BODY", payload: e.target.value })}
        />
        <FormText
          muted
          style={{ display: "flex", flexDirection: "column", fontSize: "80%" }}
        >
          <span css={{ fontWeight: "bold" }}>{t("reports:Notes")}:</span>
          <span css={{ marginLeft: "0.25rem" }}>
            {t("reports:Large data files may fail to send.")}
          </span>
          <span css={{ marginLeft: "0.25rem" }}>
            {t(
              "reports:Last tab of the report must contain the data table you want exported.",
            )}
          </span>
        </FormText>
      </FormGroup>
    </>
  );
};

export const EmailReportModal: React.FC<EmailReportModalProps> = ({
  show,
  hide,
  report,
}) => {
  const { t } = useTranslation("reports");

  const sendEmailMutation = useSendEmailReport();

  const [data, dispatch] = useReducer(formDataReducer, initialEmailFormState);
  const [errorText, setErrorText] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);

  useEffect(() => {
    if (show === false) {
      dispatch({ type: "RESET", payload: initialEmailFormState });
      setShowError(false);
      sendEmailMutation.reset();
    }
  }, [show]);

  useEffect(() => {
    if (sendEmailMutation.isSuccess) {
      hide();
    }

    if (sendEmailMutation.isError) {
      setErrorText(
        sendEmailMutation.error?.message || t("reports:Something went wrong"),
      );
      setShowError(true);
    }
  }, [
    sendEmailMutation.isSuccess,
    sendEmailMutation.isError,
    sendEmailMutation.error,
    t,
    hide,
  ]);

  const handleSendEmail = async () => {
    const { error, errorText } = validateEmailForm(data, t);
    if (error) {
      setErrorText(errorText);
      setShowError(true);
      return;
    }

    sendEmailMutation.mutate({
      reportId: report.id,
      sendTo: formatEmail(data.email),
      body: data.body,
    });
  };

  return (
    <Modal
      backdrop="static"
      show={show}
      onHide={() => hide()}
      data-qa="email-report-modal"
    >
      <Modal.Header title={t("reports:Send Email")} />
      <Modal.Body>
        {showError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setShowError(false)}
            data-qa="email-error-alert"
          >
            {errorText}
          </Alert>
        )}
        <EmailForm email={data.email} body={data.body} dispatch={dispatch} />
      </Modal.Body>
      <Modal.Footer>
        <FormButton
          label={t("reports:Cancel")}
          disabled={sendEmailMutation.isPending}
          clickHandler={() => hide()}
          data-qa="email-cancel-button"
        />
        <FormButton
          actionType="ACTION"
          label={
            sendEmailMutation.isPending
              ? t("reports:Sending...")
              : t("reports:Send")
          }
          disabled={sendEmailMutation.isPending}
          clickHandler={handleSendEmail}
          data-qa="email-send-button"
        />
      </Modal.Footer>
    </Modal>
  );
};
